import React, { useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { AppBar, Box, Button, Container, IconButton, Snackbar, Toolbar, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import ASPrintIcon from '../../../../assets/icons/Print.svg';
import ASShareIcon from '../../../../assets/icons/ShareHandoutFlight.svg';
import ASCustomiseIcon from '../../../../assets/icons/CustomiseHandout.svg';
import DisplayHandout from '../display-handout';
import QuickShareDialog from '../quick-share-dialog';
import PatientDetailsDialog from '../patient-details-dialog';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';

const actionButtonSx = {
    height: 50,
    padding: '15px 20px',
    borderRadius: '200px',
    backgroundColor: '#FF669B',
    color: '#FFFFFF',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '18px',
    letterSpacing: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 'auto',
    boxShadow: '0px 0px 10px rgba(35, 7, 88, 0.1)',
    '&:hover': { backgroundColor: '#e65588' },
    '&.Mui-disabled': { backgroundColor: '#FF669B', color: '#FFFFFF', opacity: 0.45 },
};

const iconButtonSx = {
    width: 50,
    height: 50,
    borderRadius: '50%',
    backgroundColor: '#FF669B',
    '&:hover': { backgroundColor: '#e65588' },
    '&.Mui-disabled': { backgroundColor: '#FF669B', opacity: 0.45 },
};

const mobilePillButtonSx = {
    height: 47,
    borderRadius: '188px',
    backgroundColor: '#FF669B',
    color: '#FFFFFF',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '9.4px',
    px: '18.8px',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '14px',
    letterSpacing: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 'auto',
    boxShadow: '0px 0px 9.4px rgba(35, 7, 88, 0.1)',
    '&:hover': { backgroundColor: '#e65588' },
    '&.Mui-disabled': { backgroundColor: '#FF669B', color: '#FFFFFF', opacity: 0.45 },
};

const mobileIconCircleSx = {
    width: 47,
    height: 47,
    borderRadius: '50%',
    backgroundColor: '#FF669B',
    padding: 0,
    '&:hover': { backgroundColor: '#e65588' },
    '&.Mui-disabled': { backgroundColor: '#FF669B', opacity: 0.45 },
};

const BuildYourOwnHandoutPreview = observer(() => {
    const { handoutId } = useParams();
    const navigate = useNavigate();
    const { authStore, imageStore, userCreatedHandoutStore, userDataStore } = useContext(StoreContext);

    const [handoutData, setHandoutData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showLabels, setShowLabels] = useState(true);
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');

    const isPublished = handoutData?.contentStatus === 'published';
    const usesLibraryImage = useMemo(
        () => Boolean(handoutData?.selectedLibraryImage?.imageKey),
        [handoutData],
    );
    const renderHandoutData = useMemo(
        () =>
            handoutData
                ? {
                      ...handoutData,
                      contentId: handoutData.handoutId || handoutData.contentId,
                      contentType: 'userCreatedHandout',
                  }
                : null,
        [handoutData],
    );
    const doctorName = [userDataStore?.userData?.firstName, userDataStore?.userData?.lastName]
        .filter(Boolean)
        .join(' ');
    const clinicName = userDataStore?.userData?.clinicName || '';
    const shareHandoutData = useMemo(
        () =>
            handoutData
                ? {
                      ...handoutData,
                      contentId: handoutData.handoutId || handoutData.contentId,
                      userCreatedHandoutId: handoutData.handoutId || handoutData.contentId,
                      contentType: 'userCreatedHandout',
                  }
                : null,
        [handoutData],
    );

    useEffect(() => {
        let isMounted = true;
        const loadHandout = async () => {
            if (!userCreatedHandoutStore?.isInitialized) {
                setIsLoading(true);
                return;
            }

            setIsLoading(true);
            try {
                const cached = await userCreatedHandoutStore?.getUserCreatedHandoutById(handoutId);
                const response = cached ? null : await UserApi.getUserCreatedHandout(authStore.idToken, handoutId);
                const record = cached || response?.userCreatedHandout || response?.handout;
                if (isMounted) {
                    setHandoutData(record || null);
                }
            } catch (error) {
                console.error('Failed to load user-created handout preview:', error);
                if (isMounted) {
                    setSnackbarStatus('error');
                    setSnackbarMessage('Failed to load handout.');
                    setSnackbarOpen(true);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadHandout();
        return () => {
            isMounted = false;
        };
    }, [
        authStore.idToken,
        handoutId,
        userCreatedHandoutStore,
        userCreatedHandoutStore?.isInitialized,
        userCreatedHandoutStore?.userCreatedHandoutData,
    ]);

    const notifyPublishRequired = () => {
        setSnackbarStatus('error');
        setSnackbarMessage('Publish this handout before sharing or printing.');
        setSnackbarOpen(true);
    };

    const handleShare = () => {
        if (!isPublished) {
            notifyPublishRequired();
            return;
        }
        setQuickShareOpen(true);
    };

    const handlePrint = () => {
        if (!isPublished) {
            notifyPublishRequired();
            return;
        }
        setPatientDialogOpen(true);
    };

    const handlePatientDetailsSubmit = (patientDetails) => {
        setPatientDialogOpen(false);
        const url = new URL(window.location.origin + `/handout/${shareHandoutData.contentId}/pdf`);
        url.searchParams.append('showLabels', showLabels);
        url.searchParams.append('userCreatedHandoutId', shareHandoutData.userCreatedHandoutId);

        if (patientDetails?.patientName) {
            url.searchParams.append('patientName', patientDetails.patientName);
        }
        if (patientDetails?.patientSurname) {
            url.searchParams.append('patientSurname', patientDetails.patientSurname);
        }

        window.open(url.toString(), '_blank');
    };

    return (
        <Box sx={{ background: '#F0F0FC', minHeight: 'calc(100vh - 140px)', pb: { xs: '98px', md: 0 } }}>
            <BlockingSpinner open={isLoading} />
            <AppBar
                position="static"
                color="transparent"
                elevation={0}
                sx={{
                    display: { xs: 'none', md: 'block' },
                    background: '#FFFFFF',
                    borderTop: '1px solid #e5e5e5',
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        minHeight: '82px !important',
                        px: { xs: '16px', md: '40px' },
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={isPublished ? 'Share handout' : 'Publish before sharing'} arrow>
                            <span>
                                <Button onClick={handleShare} disabled={!isPublished} sx={actionButtonSx}>
                                    <img src={ASShareIcon} alt="" style={{ width: 26, height: 26 }} />
                                    Share handout
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={isPublished ? 'Print' : 'Publish before printing'} arrow>
                            <span>
                                <IconButton onClick={handlePrint} disabled={!isPublished} sx={iconButtonSx}>
                                    <img
                                        src={ASPrintIcon}
                                        alt="Print"
                                        style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                    <Button
                        onClick={() => navigate(`/build-handout/${handoutId}`)}
                        sx={actionButtonSx}
                    >
                        <img src={ASCustomiseIcon} alt="" style={{ width: 22, height: 22 }} />
                        Edit
                    </Button>
                </Toolbar>
            </AppBar>

            <AppBar
                position="static"
                color="transparent"
                elevation={0}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    background: '#FFFFFF',
                    borderTop: '1px solid #E5E5E5',
                    borderBottom: '1px solid #E5E5E5',
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: 'flex-end',
                        minHeight: '85px !important',
                        px: '19px',
                    }}
                >
                    <Button
                        onClick={() => navigate(`/build-handout/${handoutId}`)}
                        sx={mobilePillButtonSx}
                    >
                        <img src={ASCustomiseIcon} alt="" style={{ width: 17, height: 17 }} />
                        Edit
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ py: { xs: 3, md: 5 }, px: { xs: 0, md: 0 } }}>
                {renderHandoutData ? (
                    <Container
                        component="section"
                        maxWidth="md"
                        sx={{
                            width: '100%',
                            overflowY: 'auto',
                            backgroundColor: '#FFFFFF',
                            borderRadius: { xs: 0, md: '30px 30px 0 0' },
                        }}
                        className="handout-container"
                    >
                        <Box sx={{ my: 2 }} component="section">
                            <DisplayHandout
                                handoutData={renderHandoutData}
                                showLabels={showLabels}
                                setShowLabels={setShowLabels}
                                show3DModel={true}
                                showMainIllustration={true}
                                showTutorialVideo={true}
                                imageStore={imageStore}
                                showDisclaimer={usesLibraryImage}
                                doctorName={doctorName}
                                clinicName={clinicName}
                                disableCustomiseMode
                            />
                        </Box>
                    </Container>
                ) : (
                    !isLoading && (
                        <Typography sx={{ color: '#000453', textAlign: 'center', fontSize: 24 }}>
                            Handout not found.
                        </Typography>
                    )
                )}
            </Box>

            {shareHandoutData && (
                <QuickShareDialog
                    open={quickShareOpen}
                    handout={shareHandoutData}
                    onClose={() => setQuickShareOpen(false)}
                />
            )}

            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => setPatientDialogOpen(false)}
                onSubmit={handlePatientDetailsSubmit}
            />

            <AppBar
                position="fixed"
                color="default"
                sx={{
                    display: { xs: 'block', md: 'none' },
                    top: 'auto',
                    bottom: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'none',
                    borderTop: '1px solid #E5E5E5',
                    px: '19px',
                    pb: 'env(safe-area-inset-bottom)',
                    '@media print': { display: 'none' },
                }}
            >
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: '85px', px: 0 }}>
                    <Tooltip title={isPublished ? 'Share handout' : 'Publish before sharing'} arrow>
                        <span>
                            <Button
                                onClick={handleShare}
                                disabled={!isPublished}
                                sx={{ ...mobilePillButtonSx, width: '158.988px' }}
                            >
                                <img src={ASShareIcon} alt="" style={{ width: 17, height: 17 }} />
                                Share handout
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={isPublished ? 'Print' : 'Publish before printing'} arrow>
                        <span>
                            <IconButton onClick={handlePrint} disabled={!isPublished} sx={mobileIconCircleSx}>
                                <img
                                    src={ASPrintIcon}
                                    alt="Print"
                                    style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }}
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                action={snackbarStatus === 'success' ? <CheckIcon sx={{ color: 'white' }} /> : <CloseIcon sx={{ color: 'red' }} />}
            />
        </Box>
    );
});

export default BuildYourOwnHandoutPreview;
